import { useQuery } from '@tanstack/react-query'
import { Badge, EmptyState, PageHeader, formatCurrency, type BadgeVariant } from '@lipaflow/ui'
import { apiErrorMessage } from '@lipaflow/shared'
import { adminService, type BillingHealthIssue } from '../services/admin'
import { usePageTitle } from '../lib/pageTitle'
import { useSector } from '../sector/sector'

const KIND: Record<BillingHealthIssue['kind'], string> = {
  unpriced: 'No price in force',
  calendar_missing: 'Term dates not published',
  renewal_not_raised: 'Renewal not raised',
  overdue: 'Overdue',
}
const SEVERITY: Record<BillingHealthIssue['severity'], { label: string; variant: BadgeVariant }> = {
  critical: { label: 'Fix now', variant: 'clay' },
  warning: { label: 'Needs attention', variant: 'warning' },
  info: { label: 'For information', variant: 'neutral' },
}
const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })

/**
 * Billing health: what could stop the sector's customers being billed
 * right, worst first (a product with no price, a size needing a contract,
 * term dates a renewal would need, renewals not raised, overdue invoices),
 * and what renews in the next two weeks.
 */
export default function BillingHealthPage() {
  usePageTitle('Billing health')
  const sector = useSector()
  const health = useQuery({ queryKey: ['billing-health', sector.code], queryFn: () => adminService.billingHealth(sector.code) })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Billing health"
        subtitle={`What could stop ${sector.name} customers being billed correctly, and what renews soon.`}
      />

      {health.isLoading ? (
        <p role="status" className="text-body-sm text-ink-muted">Checking…</p>
      ) : health.isError || !health.data ? (
        <p role="alert" className="text-body-sm text-warning">{apiErrorMessage(health.error, "Couldn't check billing.")}</p>
      ) : (
        <>
          <section aria-labelledby="health-issues" className="space-y-3">
            <h2 id="health-issues" className="font-outfit text-h4 text-ink">To look at</h2>
            {health.data.issues.length === 0 ? (
              <EmptyState title="Nothing to look at" description="Every customer can be priced and renewed." className="py-8" />
            ) : (
              <ul className="divide-y divide-hairline rounded-card border border-hairline bg-surface">
                {health.data.issues.map((issue, i) => (
                  <li key={`${issue.kind}-${issue.tenant_id ?? 'all'}-${i}`} className="flex flex-wrap items-start justify-between gap-3 p-4">
                    <div className="min-w-0 space-y-1">
                      <p className="flex flex-wrap items-center gap-2">
                        <Badge variant={SEVERITY[issue.severity].variant} size="sm">{SEVERITY[issue.severity].label}</Badge>
                        <span className="font-medium text-ink">{KIND[issue.kind]}</span>
                        {issue.tenant_name && <span className="text-ink-muted">· {issue.tenant_name}</span>}
                      </p>
                      <p className="text-body-sm text-ink-muted">{issue.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="renewing-soon" className="space-y-3">
            <h2 id="renewing-soon" className="font-outfit text-h4 text-ink">Renewing in the next two weeks</h2>
            {health.data.renewing_soon.length === 0 ? (
              <p className="text-body-sm text-ink-muted">No renewals due in the next two weeks.</p>
            ) : (
              <ul className="divide-y divide-hairline rounded-card border border-hairline bg-surface">
                {health.data.renewing_soon.map((row) => (
                  <li key={row.tenant_id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{row.tenant_name}</p>
                      <p className="text-body-sm text-ink-muted">
                        {row.plan === 'yearly' ? 'A year up front' : 'Each period'}
                        {row.ends_at && ` · paid to ${day(row.ends_at)} (${row.days_left ?? 0} days)`}
                      </p>
                    </div>
                    <span className="flex flex-wrap items-center gap-2">
                      {row.cancelling && <Badge variant="warning" size="sm">Stops at period end</Badge>}
                      {row.invoice ? (
                        <Badge variant={row.invoice.status === 'paid' ? 'success' : 'info'} size="sm">
                          {row.invoice.number} · {formatCurrency(row.invoice.amount, true)} · {row.invoice.status === 'paid' ? 'paid' : 'unpaid'}
                        </Badge>
                      ) : (
                        !row.cancelling && <Badge variant="neutral" size="sm">Not raised yet</Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  )
}
